import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { Select } from "antd";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { LayerSection } from "./LayerSection";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useState } from "react";

type LayerDrawerContentProps = {
  content: {
    title: string;
    items: {
      label: string;
      icon: JSX.Element;
      environmentalVariable?: EnvironmentalVariables;
      onClick: () => void;
      isSelected: boolean;
    }[];
    emptyItemsText?: string;
  }[];
};

const LayerDrawerContent: React.FC<LayerDrawerContentProps> = ({ content }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "layer",
  });

  const [selectedLayer, setSelectedLayer] = useState<string | undefined>(() => {
    const saved = localStorage.getItem("initialMapLayer");
    return saved === "noInitialMapLayer"
      ? undefined
      : saved || EnvironmentalVariables.WIND_VEL;
  });

  const allItems = content.flatMap((group) => group.items);
  const initialMapLayer = localStorage.getItem("initialMapLayer");

  const hasValue = !!selectedLayer;

  const handleChange = (e: string | undefined) => {
    if (e) {
      localStorage.setItem("initialMapLayer", e);
      setSelectedLayer(e);
    } else {
      localStorage.setItem("initialMapLayer", "noInitialMapLayer");
      setSelectedLayer(undefined);
    }
  };

  return (
    <Box>
      <Box boxSizing="border-box" px="4px">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px"
          borderColor="gray.700"
          mb={2}
          mt={4}
        >
          <Text fontSize="xs" color="gray.700">
            {t("startUpLayer")}
          </Text>
          <Tooltip
            label={t("initialLayer")}
            fontSize="sm"
            placement="top-end"
            hasArrow
          >
            <InfoOutlinedIcon style={{ color: "#6A7578" }} fontSize="small" />
          </Tooltip>
        </Flex>

        <Flex color="gray.700" alignItems="center" justifyContent="end" gap={2}>
          <style>
            {`
              .custom-blue-border .ant-select-selector {
                border: 1px solid #555353 !important;
                color: #555353;
                border-radius: 4px;
                transition: border-color 0.3s ease;
              }

              .custom-blue-border .ant-select-selector:hover {
                border-color: #3e9ed7 !important;
                color: #3e9ed7 !important;
              }
              .custom-blue-border .ant-select-clear {
                opacity: 1 !important;
                visibility: visible !important;
              }

              .custom-blue-border .ant-select-selector:hover .ant-select-clear {
                opacity: 1 !important;
              }
            `}
          </style>

          <Select
            className="custom-blue-border"
            showSearch
            placeholder={t("typeLayer")}
            optionFilterProp="children"
            style={{ width: "100%", color: "red" }}
            dropdownStyle={{ zIndex: 2000 }}
            defaultValue={
              !initialMapLayer
                ? EnvironmentalVariables.WIND_VEL
                : initialMapLayer === "noInitialMapLayer"
                ? undefined
                : initialMapLayer
            }
            suffixIcon={hasValue ? null : undefined}
            onChange={handleChange}
            allowClear
          >
            {allItems.map((x) => (
              <Select.Option value={x.environmentalVariable} key={x.label}>
                {x.label}
              </Select.Option>
            ))}
          </Select>
        </Flex>
        {content.map((content) => (
          <LayerSection
            key={content.title}
            title={content.title}
            items={content.items}
            emptyItemsText={content.emptyItemsText}
          />
        ))}
      </Box>
    </Box>
  );
};

export default observer(LayerDrawerContent);
